import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Briefcase, UserPlus, Mail, Trash2, RefreshCw, ShieldOff, ShieldCheck, X, AlertTriangle, CheckCircle2, PoundSterling, TrendingUp, Building2, Copy, Save } from "lucide-react";
import { fmtDateTime } from "../lib/datetime";

const TABS = [
  { id: "accounts", label: "McCain accounts", icon: UserPlus },
  { id: "commercial", label: "Commercial dashboard", icon: PoundSterling },
];

export default function McCainFolder() {
  const [tab, setTab] = useState("accounts");
  return (
    <div data-testid="mccain-folder-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 18 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>
          NEWELL · INTERNAL · ADMIN + CONTRACTS MANAGER ONLY
        </div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0", display: "flex", alignItems: "center", gap: 10 }}>
          <Briefcase size={26} color="#0f172a" /> McCain Folder
        </h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>The single Newell-internal hub for the McCain contract — manage McCain access AND see the money. Never visible to any McCain user, regardless of tier.</div>
      </header>

      <div style={{ display: "flex", borderBottom: "1px solid #e2e8f0", marginBottom: 18, gap: 4 }}>
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`mcf-tab-${id}`} onClick={() => setTab(id)} className="btn-ghost"
            style={{ border: "none", borderRadius: 0, borderBottom: tab === id ? "3px solid #ff5a00" : "3px solid transparent", padding: "10px 14px", fontWeight: 700, background: "transparent", color: tab === id ? "#0f172a" : "#64748b" }}>
            <Icon size={14} style={{ verticalAlign: -2, marginRight: 6 }} />{label}
          </button>
        ))}
      </div>

      {tab === "accounts" ? <AccountsTab /> : <CommercialTab />}
    </div>
  );
}

/* ---------- Accounts tab ---------- */
function AccountsTab() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [activationModal, setActivationModal] = useState(null); // {link, expires_at, email_sent}

  const load = async () => {
    try { const { data } = await api.get("/mccain-folder/accounts"); setRows(data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const changeTier = async (u, newTier) => {
    if (u.tier === newTier) return;
    try { await api.patch(`/mccain-folder/accounts/${u.id}`, { tier: newTier }); setSuccess(`${u.name} → ${newTier}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleDisabled = async (u) => {
    try { await api.patch(`/mccain-folder/accounts/${u.id}`, { disabled: u.status !== "disabled" ? true : false }); setSuccess(`${u.name} ${u.status !== "disabled" ? "disabled" : "re-enabled"}`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const resend = async (u) => {
    try {
      const { data } = await api.post(`/mccain-folder/accounts/${u.id}/resend-invite`);
      setActivationModal({ link: data.activation_link, expires_at: data.expires_at, email_sent: data.email_sent, email_reason: data.email_reason, who: u.name });
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const revoke = async (u) => {
    if (!window.confirm(`Revoke McCain access for ${u.name}? Any pending invites will be invalidated and they cannot log in again until re-enabled.`)) return;
    try { await api.delete(`/mccain-folder/accounts/${u.id}`); setSuccess(`${u.name} revoked`); load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="card" style={{ padding: 14, marginBottom: 14, background: "#fff7ed", borderLeft: "4px solid #ff5a00" }}>
        <div className="mono" style={{ fontSize: 11, color: "#9a3412", letterSpacing: "0.1em", marginBottom: 4 }}>HOW McCAIN ACCESS WORKS</div>
        <div style={{ fontSize: 13, color: "#0f172a", lineHeight: 1.5 }}>
          <strong>Supervisor</strong> — can request callouts and view their own register. No RAMS approval, no rota diary, no commercial data.<br />
          <strong>Management</strong> — superset. Adds RAMS approval (DRA sign-off), inspection reports, read-only rota diary, KPI dashboard. Still no commercial data.
        </div>
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        <button data-testid="mcf-new-account" className="btn-primary" onClick={() => setShowNew(true)}><UserPlus size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Invite McCain user</button>
      </div>

      {error ? <ErrorBanner message={error} onClose={() => setError("")} /> : null}
      {success ? <SuccessBanner message={success} onClose={() => setSuccess("")} /> : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="mcf-accounts-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Tier</th><th>Status</th><th>Pending invite</th><th style={{ textAlign: "right" }}>Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No McCain users yet. Click &quot;Invite McCain user&quot; to create the first one.</td></tr> :
              rows.map((u) => (
                <tr key={u.id} data-testid={`mcf-row-${u.id}`}>
                  <td data-label="Name" style={{ fontWeight: 600 }}>{u.name}</td>
                  <td data-label="Email" className="mono" style={{ fontSize: 12 }}>{u.email}</td>
                  <td data-label="Tier">
                    <select data-testid={`mcf-tier-${u.id}`} value={u.tier} onChange={(e) => changeTier(u, e.target.value)} disabled={u.status === "disabled"} style={{ padding: "2px 6px", fontSize: 12 }}>
                      <option value="supervisor">Supervisor</option>
                      <option value="management">Management</option>
                    </select>
                  </td>
                  <td data-label="Status"><StatusBadge s={u.status} /></td>
                  <td data-label="Pending invite" className="mono" style={{ fontSize: 11 }}>{u.pending_invite_expires_at ? `expires ${fmtDateTime(u.pending_invite_expires_at)}` : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                  <td data-label="Actions" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button data-testid={`mcf-resend-${u.id}`} className="btn-ghost" onClick={() => resend(u)} title="Resend activation/reset"><Mail size={12} /></button>
                    <button data-testid={`mcf-toggle-${u.id}`} className="btn-ghost" onClick={() => toggleDisabled(u)} title={u.status === "disabled" ? "Re-enable" : "Disable"}>
                      {u.status === "disabled" ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
                    </button>
                    <button data-testid={`mcf-revoke-${u.id}`} className="btn-ghost" onClick={() => revoke(u)} style={{ color: "#b91c1c" }} title="Revoke"><Trash2 size={12} /></button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {showNew ? (
        <NewAccountModal
          onClose={() => setShowNew(false)}
          onSaved={(data, who) => { setShowNew(false); setSuccess(`Invite created for ${who}`); setActivationModal({ link: data.activation_link, expires_at: data.expires_at, email_sent: data.email_sent, email_reason: data.email_reason, who }); load(); }}
          onError={(m) => setError(m)}
        />
      ) : null}
      {activationModal ? <ActivationModal data={activationModal} onClose={() => setActivationModal(null)} /> : null}
    </div>
  );
}

function StatusBadge({ s }) {
  const map = {
    active: { bg: "#dcfce7", fg: "#065f46", label: "ACTIVE" },
    pending_activation: { bg: "#fef3c7", fg: "#92400e", label: "PENDING" },
    disabled: { bg: "#fee2e2", fg: "#b91c1c", label: "DISABLED" },
  };
  const c = map[s] || { bg: "#f1f5f9", fg: "#334155", label: (s || "").toUpperCase() };
  return <span className="mono" style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>{c.label}</span>;
}

function NewAccountModal({ onClose, onSaved, onError }) {
  const [form, setForm] = useState({ name: "", email: "", tier: "supervisor" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/mccain-folder/accounts", form);
      onSaved(data, form.name);
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Invite McCain user" onClose={onClose}>
      <form onSubmit={submit}>
        <Field label="Full name *"><input data-testid="mcf-new-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Sam Bell" autoFocus /></Field>
        <Field label="Email *"><input data-testid="mcf-new-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="firstname.lastname@mccain.co.uk" /></Field>
        <Field label="Tier *">
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" data-testid="mcf-tier-supervisor" onClick={() => setForm({ ...form, tier: "supervisor" })} className={form.tier === "supervisor" ? "btn-primary" : "btn-ghost"} style={{ flex: 1, textAlign: "left", padding: 10 }}>
              <div style={{ fontWeight: 800 }}>Supervisor</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>Request callouts · view own register</div>
            </button>
            <button type="button" data-testid="mcf-tier-management" onClick={() => setForm({ ...form, tier: "management" })} className={form.tier === "management" ? "btn-primary" : "btn-ghost"} style={{ flex: 1, textAlign: "left", padding: 10 }}>
              <div style={{ fontWeight: 800 }}>Management</div>
              <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4 }}>Adds RAMS approve · rota diary · KPI</div>
            </button>
          </div>
        </Field>
        <p className="mono" style={{ fontSize: 11, color: "#64748b", marginTop: 14 }}>An activation email with a single-use link will be sent. Plaintext passwords are never used.</p>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button data-testid="mcf-new-submit" type="submit" className="btn-primary" disabled={busy}><Save size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{busy ? "Creating…" : "Invite"}</button>
        </div>
      </form>
    </Modal>
  );
}

function ActivationModal({ data, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(data.link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  return (
    <Modal title={`Activation link for ${data.who}`} onClose={onClose}>
      {data.email_sent ? (
        <div className="card" style={{ padding: 10, marginBottom: 12, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", fontSize: 13 }}>
          <CheckCircle2 size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Activation email sent successfully.
        </div>
      ) : (
        <div className="card" style={{ padding: 10, marginBottom: 12, background: "#fef3c7", color: "#92400e", borderLeft: "4px solid #f59e0b", fontSize: 13 }}>
          <AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />Email <strong>not</strong> sent ({data.email_reason || "unknown"}). Send this link manually:
        </div>
      )}
      <div className="mono" style={{ background: "#0f172a", color: "#10b981", padding: 12, borderRadius: 4, fontSize: 11, wordBreak: "break-all", marginBottom: 12 }} data-testid="mcf-activation-link">{data.link}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="mono" style={{ fontSize: 11, color: "#64748b" }}>Expires {fmtDateTime(data.expires_at)}</div>
        <button data-testid="mcf-copy-link" className="btn-primary" onClick={copy}><Copy size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />{copied ? "Copied!" : "Copy link"}</button>
      </div>
    </Modal>
  );
}

/* ---------- Commercial tab ---------- */
function CommercialTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get("/mccain-folder/commercial")
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const fmt = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (error) return <ErrorBanner message={error} />;
  if (!data) return <div style={{ color: "#64748b", padding: 24 }}>Loading commercial dashboard…</div>;

  return (
    <div data-testid="mcf-commercial">
      <div className="card" style={{ padding: 14, marginBottom: 14, background: "#0f172a", color: "#fff", borderLeft: "4px solid #ff5a00" }}>
        <div className="mono" style={{ fontSize: 11, color: "#cbd5e1", letterSpacing: "0.1em", marginBottom: 4 }}>RESTRICTED · ADMIN + CONTRACTS MANAGER ONLY</div>
        <div style={{ fontSize: 13, color: "#fff" }}>This dashboard is the precursor to the Phase 2 monthly application. Numbers are derived from completed handovers × price schedule + out-of-hours visits × callout rate 7A. <strong>Never visible to McCain.</strong></div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 24 }}>
        <Tile label="Today" value={fmt(data.running_totals.daily)} accent="#3b82f6" testid="mcf-amt-daily" />
        <Tile label="This week" value={fmt(data.running_totals.weekly)} accent="#0ea5e9" testid="mcf-amt-weekly" />
        <Tile label="This month" value={fmt(data.running_totals.monthly)} accent="#059669" testid="mcf-amt-monthly" />
        <Tile label={`Callouts (7A · ${data.callouts.month_count})`} value={fmt(data.callouts.month_amount)} accent="#dc2626" testid="mcf-amt-callouts" />
        <Tile label="Grand total · month" value={fmt(data.grand_total_month)} accent="#ff5a00" testid="mcf-amt-grand" />
      </div>

      <h2 style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "#475569", margin: "0 0 12px" }}>
        <Building2 size={14} style={{ verticalAlign: -2, marginRight: 6 }} />Structure breakdown — this month
      </h2>
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="mcf-structure-breakdown">
          <thead><tr><th>Structure</th><th>Handovers</th><th>Last ref</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
          <tbody>
            {data.structure_breakdown.length === 0 ? <tr><td colSpan={4} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No completed handovers this month.</td></tr> :
              data.structure_breakdown.map((s) => (
                <tr key={s.structure_id} data-testid={`mcf-struct-${s.structure_id}`}>
                  <td data-label="Structure" style={{ fontWeight: 600 }}>{s.structure_description}</td>
                  <td data-label="Handovers" className="mono">{s.handovers}</td>
                  <td data-label="Last ref" className="mono">{s.last_price_schedule_ref || "—"}</td>
                  <td data-label="Amount" style={{ textAlign: "right", fontWeight: 700 }}>{fmt(s.amount)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="mono" style={{ fontSize: 11, color: "#94a3b8", marginTop: 16, textAlign: "right" }}>
        Computed {fmtDateTime(data.computed_at)} · currency {data.currency}
      </div>
    </div>
  );
}

/* ---------- Shared bits ---------- */
function Tile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="card" style={{ padding: 16, borderTop: `3px solid ${accent || "#0f172a"}` }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function ErrorBanner({ message, onClose }) {
  return (
    <div data-testid="mcf-error" className="card" style={{ padding: 12, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}>
      <AlertTriangle size={16} /> {message}
      {onClose ? <button className="btn-ghost" onClick={onClose} style={{ marginLeft: "auto" }}><X size={12} /></button> : null}
    </div>
  );
}

function SuccessBanner({ message, onClose }) {
  return (
    <div data-testid="mcf-success" className="card" style={{ padding: 12, marginBottom: 12, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", gap: 8, alignItems: "center" }}>
      <CheckCircle2 size={16} /> {message}
      {onClose ? <button className="btn-ghost" onClick={onClose} style={{ marginLeft: "auto" }}><X size={12} /></button> : null}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ background: "white", maxWidth: 520, width: "100%", padding: 24, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 900 }}>{title}</h2>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
